/**
 * Centralized definition of all available lead actions.
 * The `id` field corresponds to the prompt text sent to the AI.
 * Add/remove items here to control which actions are available everywhere
 * (notification card, action chat welcome screen, etc.)
 */

export interface LeadAction {
  id: string
  label: string
  description: string
  icon: string
  /** When false, the action is hidden from the UI but kept for future use */
  enabled: boolean
  /** The prompt template used to seed the chat. Use {name} as a placeholder. */
  promptTemplate: string
}

export const LEAD_ACTIONS: LeadAction[] = [
  {
    id: 'view_details',
    label: 'View Lead Details',
    description: 'See all information about this lead',
    icon: 'User',
    enabled: true,
    promptTemplate: 'Show me the full details for {name} in HubSpot.',
  },
  {
    id: 'update_lead',
    label: 'Update Lead',
    description: 'Update properties or status',
    icon: 'Edit',
    enabled: true,
    promptTemplate: 'I want to update the HubSpot record for {name}. What properties can I change?',
  },
  {
    id: 'change_status',
    label: 'Change Lead Status',
    description: 'Update lifecycle stage or lead status',
    icon: 'ArrowRight',
    enabled: true,
    promptTemplate: 'Change the lead status for {name} in HubSpot.',
  },
  {
    id: 'send_email',
    label: 'Send Email',
    description: 'Draft and send an email to this lead',
    icon: 'Mail',
    enabled: true,
    promptTemplate: 'Help me send an email to {name}.',
  },
  {
    id: 'add_note',
    label: 'Add a Note',
    description: 'Log a note against this lead in HubSpot',
    icon: 'FileText',
    enabled: true,
    promptTemplate: 'I want to add a note to {name}\'s record in HubSpot.',
  },
  {
    id: 'create_task',
    label: 'Create Follow-up Task',
    description: 'Schedule a follow-up reminder',
    icon: 'CheckSquare',
    enabled: true,
    promptTemplate: 'Create a follow-up task for {name} in HubSpot.',
  },
  {
    id: 'qualify',
    label: 'Qualify Lead',
    description: 'Run AI-powered prospect qualification',
    icon: 'Zap',
    enabled: true,
    promptTemplate: 'Qualify {name} as a prospect for our ICP.',
  },
]

export function buildActionPrompt(action: LeadAction, leadName?: string): string {
  const name = leadName || 'this lead'
  return action.promptTemplate.replace(/{name}/g, name)
}
